import React from "react";
import { Button, Dialog } from "@/ds";
import type { RequestDetail } from "@/api";
import { Icon } from "@/lib";
import { useT } from "../../i18n";
import { workspaceView } from "../../api/workspace-view";
import { SolGuidance, type GuidanceTarget, type SolGuidanceItem } from "./sol-guidance";
import { WORKSPACE_TASKS_ID, WorkspaceTasks } from "./workspace-tasks";
import { WorkflowPanel, WorkflowCommercial } from "./workflow-panel";
import { DecisionExplanation } from "./decision-explanation";
import { reviewGuidanceSummaryKey } from "../review-guidance";
import styles from "./email-review.module.css";

export type RequestDetailsTarget = "overview" | "tasks" | "processing";

export function RequestDetailsDialog({ data, target, onClose, finalFocusRef, items, localChanges, historical = false,
  canOpenSources, onNavigate, onAssignment, reload }: {
  data: RequestDetail; target: RequestDetailsTarget | null; onClose: () => void;
  finalFocusRef: React.RefObject<HTMLElement | null>;
  items: SolGuidanceItem[]; localChanges: boolean; canOpenSources: boolean;
  onNavigate: (target: GuidanceTarget) => void; onAssignment?: () => void;
  reload: () => Promise<unknown>;
  historical?: boolean;
}) {
  const t = useT(), view = workspaceView(data);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const initialFocusRef = React.useRef<HTMLElement | null>(null);
  const focusTarget = (root: HTMLElement) => target === "tasks"
    ? root.querySelector<HTMLElement>(`#${WORKSPACE_TASKS_ID} details[open][data-task-open] input:not(:disabled)`)
      ?? root.querySelector<HTMLElement>(`#${WORKSPACE_TASKS_ID} [data-task-open] > summary`)
      ?? root.querySelector<HTMLElement>(`#${WORKSPACE_TASKS_ID}`) ?? root
    : target === "processing" ? root.querySelector<HTMLElement>("[data-processing-details]") ?? root : root;
  // Dialog owns focus on opening; a destination selected inside it can move focus immediately.
  React.useEffect(() => {
    const root = contentRef.current;
    if (!target || !root) return;
    const node = focusTarget(root);
    initialFocusRef.current = node;
    if (root.getClientRects().length) {
      node.focus({ preventScroll: true }); node.scrollIntoView({ block: "nearest" });
    }
  }, [target]);
  const disabled = historical || localChanges || !data.preparation.can_edit;
  return <Dialog open={target !== null} keepMounted wide closeButton={false}
    title={t("ws.details.title")} icon={<Icon name="fileText" size={20} />}
    onClose={onClose} initialFocusRef={initialFocusRef} finalFocusRef={finalFocusRef}
    footer={<Button variant="ghost" onClick={onClose}>{t("common.close")}</Button>}>
    <div className={styles.requestDetails} tabIndex={-1} ref={node => {
      contentRef.current = node;
      if (node) initialFocusRef.current = focusTarget(node);
    }}>
      {!historical && (view.engine === "single_agent" ? <SolGuidance key={data.workflow?.result_id ?? "no-result"}
        data={data} items={items} expanded onNavigate={onNavigate} canOpenSources={canOpenSources}
        onAssignment={onAssignment} localChanges={localChanges} taskControls={{ disabled, reload }} />
        : <><p>{t(reviewGuidanceSummaryKey(data.review_guidance))}</p>
          <WorkspaceTasks data={data} disabled={disabled} reload={reload} /></>)}
      <section data-processing-details tabIndex={-1} className="stack-sm" aria-label={t("ws.details.title")}>
        {historical && data.workflow && <WorkflowPanel data={data} reload={async () => undefined} historical />}
        {!historical && view.engine === "single_agent" && <>
          <WorkflowPanel data={{ ...data, workflow: view.activeWorkflow ?? undefined }} reload={reload} disabled={disabled} statusInGuidance />
        </>}
        {data.draft?.decision_id && data.draft.decision_id === data.decision?.id &&
          <DecisionExplanation explanation={data.decision.explanation} />}
        {!historical && view.history && (view.engine !== "single_agent" || view.history.payload) && <details><summary>{t("workflow.historical")}</summary>
          <WorkflowPanel data={{ ...data, workflow: view.history }} reload={async () => undefined} historical statusInGuidance={view.engine === "single_agent"} />
          <WorkflowCommercial workflow={view.history} />
        </details>}
      </section>
    </div>
  </Dialog>;
}
